import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/** How long a request may run before "searching" is announced to assistive technology. */
const LOADING_ANNOUNCEMENT_DELAY_MS = 600;

/** How many recently viewed products are shown while the query is empty. */
const RECENTLY_VIEWED_LIMIT = 4;

/**
 * @typedef {'idle' | 'recent' | 'loading' | 'results' | 'empty' | 'error'} SearchState
 */

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * States, exposed as `data-state` for styling:
 * - `idle`: no query and no viewing history; nothing is shown beneath the field.
 * - `recent`: no query; the products this browser viewed most recently are shown.
 * - `loading`: a request is running and there are no results for it yet.
 * - `results`: suggestions and/or products for the current query.
 * - `empty`: the request succeeded and matched nothing.
 * - `error`: the request failed; ordinary form submission remains available.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLFormElement} [form] - The search form.
 * @property {HTMLElement} [status] - The live region that announces loading and result status.
 * @property {HTMLElement} [viewAllLabel] - The label of the all-results action.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * The request currently allowed to update the results.
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** Increases with every request, clear and close, so late responses can be recognised and dropped. */
  #requestId = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #loadingAnnouncementTimer;

  #selectedIndex = -1;

  /**
   * The recently viewed rows last fetched, kept for this page view so reopening and clearing are instant.
   * @type {{ key: string, results: HTMLElement } | null}
   */
  #recentlyViewedCache = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /** @returns {SearchState} */
  get state() {
    return /** @type {SearchState} */ (this.dataset.state ?? 'idle');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;
    const { searchInput, form } = this.refs;

    this.#toggleResetButton();

    searchInput.addEventListener('compositionend', this.#handleCompositionEnd, { signal });
    form?.addEventListener('submit', this.#handleSubmit, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#invalidateRequests();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /** Closing search discards the query, the results and anything still in flight. */
  #handleDialogClose = () => {
    this.#clear({ showRecentlyViewed: false });
  };

  /** Opening search with an empty field shows this browser's recently viewed products, if it has any. */
  #handleDialogOpen = () => {
    if (this.refs.searchInput.value.trim().length === 0) this.#showIdle();
  };

  /** A finished composition (IME) is a real change to the query; the keystrokes before it are not. */
  #handleCompositionEnd = () => {
    this.search.cancel();
    this.#update();
  };

  /**
   * Blocks empty and whitespace-only submissions; anything else goes to the full search results page.
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (this.refs.searchInput.value.trim().length > 0) return;

    event.preventDefault();
    this.refs.searchInput.focus();
  };

  /** @returns {HTMLElement[]} Suggestion and product rows in display order. */
  get #resultItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.predictiveSearchResults.querySelectorAll('[data-search-result]'))
    );
  }

  /**
   * Marks one result as the active option for the combobox, or none when index is -1.
   * @param {number} index
   * @param {{ scroll?: boolean }} [options]
   */
  #select(index, { scroll = true } = {}) {
    const { searchInput } = this.refs;
    const items = this.#resultItems;
    const selected = items[index] ?? null;

    this.#selectedIndex = selected ? index : -1;

    for (const item of items) {
      item.setAttribute('aria-selected', String(item === selected));
    }

    if (selected?.id) {
      searchInput.setAttribute('aria-activedescendant', selected.id);
      if (scroll) selected.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * Arrow keys move through the results while focus stays in the field; Enter opens the active result.
   * Without an active result Enter is left alone so the form submits to the full search results page.
   * Tab is not intercepted, so the clear, submit and close buttons stay reachable.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.isComposing) return;

    const items = this.#resultItems;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!items.length) return;
        event.preventDefault();

        const step = event.key === 'ArrowDown' ? 1 : -1;
        // Positions run from -1 (the field itself) to the last result, wrapping at both ends.
        const positions = items.length + 1;
        const next = ((this.#selectedIndex + 1 + step + positions) % positions) - 1;
        this.#select(next);
        break;
      }

      case 'Enter': {
        const link = items[this.#selectedIndex]?.querySelector('a');
        if (!link) return;

        event.preventDefault();
        link.click();
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Clears the query and results without closing search, keeping focus in the field.
   */
  resetSearch = () => {
    this.#clear();
    this.refs.searchInput.focus();
  };

  /**
   * Debounced input handler.
   * @param {InputEvent} event
   */
  search = debounce((event) => {
    // Ignore input events that are not text edits, and keystrokes inside an unfinished composition.
    if (!event.inputType || event.isComposing) return;

    this.#update();
  }, 200);

  /** Brings the results in line with the field's current value. */
  #update() {
    const searchTerm = this.refs.searchInput.value.trim();

    this.#toggleResetButton();

    // No search is requested for an empty or whitespace-only query, or once search has closed.
    if (!searchTerm.length || this.dialog?.refs.dialog.open === false) {
      this.#showIdle({ showRecentlyViewed: this.dialog?.refs.dialog.open !== false });
      return;
    }

    this.#getSearchResults(searchTerm);
  }

  /** Stops the current request from ever updating the results. */
  #invalidateRequests() {
    this.#requestId += 1;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    clearTimeout(this.#loadingAnnouncementTimer);
  }

  /**
   * Fetches the results section for a query from the predictive search endpoint.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    this.#invalidateRequests();
    const requestId = this.#requestId;
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.dataset.sectionId);

    const deferredPromise = SearchUpdateEvent.createPromise();
    // Listeners may not handle a rejection for a superseded or failed request.
    deferredPromise.promise.catch(() => {});

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    this.#showLoading(searchTerm);

    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) throw new Error(`Predictive search responded with ${response.status}`);

      const markup = await response.text();
      if (requestId !== this.#requestId) {
        deferredPromise.reject(new Error('Superseded by a newer search'));
        return;
      }

      const results = new DOMParser().parseFromString(markup, 'text/html').getElementById('predictive-search-results');
      if (!results) throw new Error('Predictive search returned no results markup');

      this.#showResults(results, searchTerm);
      deferredPromise.resolve({ totalCount: this.#resultItems.length });
    } catch (error) {
      deferredPromise.reject(error);

      // An aborted or superseded request is not a failure the customer should hear about.
      if (abortController.signal.aborted || requestId !== this.#requestId) return;

      this.#showError(searchTerm);
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /**
   * @param {SearchState} state
   * @param {string} [searchTerm]
   */
  #setState(state, searchTerm = '') {
    const { searchInput, predictiveSearchResults, viewAllLabel } = this.refs;

    this.dataset.state = state;
    searchInput.setAttribute('aria-expanded', String(state !== 'idle'));
    predictiveSearchResults.setAttribute('aria-busy', String(state === 'loading'));

    if (viewAllLabel) {
      const template = this.dataset.allResultsLabel ?? '';
      // A replacer function keeps characters such as "$&" in the query from being treated as patterns.
      viewAllLabel.textContent = searchTerm ? template.replace('[query]', () => searchTerm) : '';
    }
  }

  /** @param {string} message */
  #announce(message) {
    const { status } = this.refs;
    if (status) status.textContent = message;
  }

  /**
   * Replaces the results with a single message.
   * @param {string} message
   */
  #showMessage(message) {
    const paragraph = document.createElement('p');
    paragraph.className = 'search-results__message';
    paragraph.textContent = message;
    this.refs.predictiveSearchResults.replaceChildren(paragraph);
  }

  /**
   * The empty-query state: recently viewed products when there are any, otherwise nothing.
   * @param {{ showRecentlyViewed?: boolean }} [options]
   */
  #showIdle({ showRecentlyViewed = true } = {}) {
    this.#invalidateRequests();
    this.#select(-1);
    this.refs.predictiveSearchResults.replaceChildren();
    this.#announce('');
    this.#setState('idle');

    if (showRecentlyViewed) this.#showRecentlyViewed();
  }

  /**
   * Product ids this browser has viewed, most recent first, without duplicates.
   * Storage that is blocked, empty or holds something unexpected simply means no history.
   * @returns {string[]}
   */
  #getRecentlyViewedIds() {
    try {
      const stored = RecentlyViewed.getProducts();
      if (!Array.isArray(stored)) return [];

      const ids = stored.map((id) => String(id)).filter((id) => /^\d+$/.test(id));
      return [...new Set(ids)].slice(0, RECENTLY_VIEWED_LIMIT);
    } catch {
      return [];
    }
  }

  /**
   * Fetches the recently viewed products by id from the ordinary search endpoint and shows them in viewing
   * order. Any failure leaves the area beneath the field empty: viewing history is a convenience, not a result.
   */
  async #showRecentlyViewed() {
    const ids = this.#getRecentlyViewedIds();
    if (!ids.length || !this.dataset.sectionId) return;

    const key = ids.join(',');
    if (this.#recentlyViewedCache?.key === key) {
      this.#renderRecentlyViewed(this.#recentlyViewedCache.results);
      return;
    }

    // Shares the request counter with searches, so typing, clearing or closing discards a late answer.
    const requestId = this.#requestId;
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) return;

      const markup = await response.text();
      if (requestId !== this.#requestId) return;

      const results = new DOMParser().parseFromString(markup, 'text/html').getElementById('predictive-search-results');
      const list = results?.querySelector('[data-recently-viewed-list]');
      if (!results || !list) return;

      // Search returns the products in its own order; put them back in the order they were viewed.
      /** @type {Map<string, HTMLElement>} */
      const rowsById = new Map();
      for (const row of list.querySelectorAll('[data-product-id]')) {
        if (row instanceof HTMLElement && row.dataset.productId) rowsById.set(row.dataset.productId, row);
      }

      const orderedRows = ids.flatMap((id) => rowsById.get(id) ?? []);
      if (!orderedRows.length) return;

      list.replaceChildren(...orderedRows);

      this.#recentlyViewedCache = { key, results };
      this.#renderRecentlyViewed(results);
    } catch {
      // Network failure or abort: nothing to show.
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /** @param {HTMLElement} results - The recently viewed root kept in the cache. */
  #renderRecentlyViewed(results) {
    this.refs.predictiveSearchResults.replaceChildren(document.importNode(results, true));
    this.#select(-1);
    this.#setState('recent');
  }

  /**
   * While a request runs, earlier results stay in place but dimmed (marked busy) so the panel does not
   * jump; with nothing to keep, a short message is shown instead.
   * @param {string} searchTerm
   */
  #showLoading(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const hadResults = this.state === 'results';

    this.#select(-1);

    if (hadResults) {
      this.#setState('results', searchTerm);
      predictiveSearchResults.setAttribute('aria-busy', 'true');
    } else {
      this.#showMessage(this.dataset.loadingText ?? '');
      this.#setState('loading', searchTerm);
    }

    // Quick responses are not announced as loading; only a wait long enough to notice is.
    this.#loadingAnnouncementTimer = setTimeout(() => {
      this.#announce(this.dataset.loadingText ?? '');
    }, LOADING_ANNOUNCEMENT_DELAY_MS);
  }

  /**
   * @param {HTMLElement} results - The results root parsed from the section response.
   * @param {string} searchTerm
   */
  #showResults(results, searchTerm) {
    const { predictiveSearchResults } = this.refs;

    clearTimeout(this.#loadingAnnouncementTimer);

    const state = results.dataset.searchState === 'empty' ? 'empty' : 'results';
    const statusText = results.querySelector('[data-search-status]')?.textContent?.trim() ?? '';

    predictiveSearchResults.replaceChildren(document.importNode(results, true));
    predictiveSearchResults.scrollTo(0, 0);

    this.#select(-1);
    this.#setState(state, searchTerm);
    this.#announce(statusText);
  }

  /** @param {string} searchTerm */
  #showError(searchTerm) {
    clearTimeout(this.#loadingAnnouncementTimer);

    const message = this.dataset.errorText ?? '';
    this.#select(-1);
    this.#showMessage(message);
    this.#setState('error', searchTerm);
    this.#announce(message);
  }

  #toggleResetButton() {
    const { resetButton, searchInput } = this.refs;

    resetButton.hidden = searchInput.value.length === 0;
  }

  /**
   * Empties the field and returns to the empty-query state, dropping any pending or debounced request.
   * @param {{ showRecentlyViewed?: boolean }} [options]
   */
  #clear(options) {
    this.search.cancel();
    this.refs.searchInput.value = '';
    this.#toggleResetButton();
    this.#showIdle(options);
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
